import { useEffect, useMemo, useState } from 'react';
import { bridgeAuctionInfo, bridgeBidAllowed, bridgePartnerOf, bridgeTeamOf, sortHand, type BridgeCall, type BridgeLegal, type BridgeView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

const COMPASS = ['N', 'E', 'S', 'W'];
const STRAIN_SYMBOL: Record<string, string> = { C: '♣', D: '♦', H: '♥', S: '♠', N: 'NT' };
const STRAINS = ['C', 'D', 'H', 'S', 'N'] as const;
const isRed = (s: string) => s === 'H' || s === 'D';

function callText(c: BridgeCall): string {
  return c.kind === 'pass' ? 'Pass' : c.kind === 'double' ? 'X' : c.kind === 'redouble' ? 'XX' : `${c.level}${STRAIN_SYMBOL[c.strain]}`;
}

function CallBadge({ call }: { call: BridgeCall }) {
  const red = call.kind === 'bid' && isRed(call.strain);
  return <span style={{ color: red ? '#ff8a8a' : undefined, fontWeight: call.kind === 'pass' ? 400 : 700 }}>{callText(call)}</span>;
}

export function BridgeTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as BridgeView;
  const legal = useGame((s) => s.legal) as BridgeLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const dummyHand = useMemo(() => (view.dummyHand ? sortHand(view.dummyHand) : null), [view.dummyHand]);
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const bidding = view.phase === 'BIDDING';
  const playing = view.phase === 'PLAYING' || view.phase === 'TRICK_END';
  const contract = view.contract;
  const myTeam = mySeat !== null ? bridgeTeamOf(mySeat) : 0;
  const playFrom = legal?.kind === 'play' ? legal.from : null;
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const dealt = view.dealResults.at(-1);
  const dealOver = view.phase === 'DEAL_END' || view.phase === 'MATCH_END';
  const iAmDummy = mySeat !== null && view.dummy === mySeat;

  const playCard = (c: string) => {
    if (!myTurn || legal?.kind !== 'play' || !playable.has(c) || acting) return;
    void act({ type: 'play', card: c });
  };

  const contractText = contract ? `${contract.level}${STRAIN_SYMBOL[contract.strain]}${contract.doubled === 1 ? ' X' : contract.doubled === 2 ? ' XX' : ''}` : null;

  // The auction table: one column per seat, a row per round, starting with the dealer's cell.
  const rows = useMemo(() => {
    const out: (BridgeCall | null)[][] = [];
    view.calls.forEach((c, i) => {
      const r = Math.floor((i + view.dealer) / 4);
      (out[r] ??= [null, null, null, null])[c.seat] = c.call;
    });
    if (out.length === 0) out.push([null, null, null, null]);
    return out;
  }, [view.calls, view.dealer]);

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{COMPASS[s]} · {view.teamScores[bridgeTeamOf(s)] ?? 0}</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {mySeat !== null && s === bridgePartnerOf(mySeat) && !contract && <span className="tag">{t('sp.partner')}</span>}
                {contract && contract.declarer === s && <span className="tag seen">{t('br.declarer')}</span>}
                {view.dummy === s && <span className="tag blind">{t('br.dummy')}</span>}
                {view.vulnerable[bridgeTeamOf(s)] && s < 2 && <span className="tag" title={t('br.vul')}>V</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('br.deal', { n: view.dealNo, total: view.config.deals })}
            {contractText && ` · ${contractText} ${t('br.by', { name: COMPASS[contract!.declarer]! })}`}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 6, flexWrap: 'wrap' }} aria-label={t('table.scores')}>
            {[myTeam, 1 - myTeam].map((team) => (
              <span key={team} className={`pill team-pill ${team === myTeam ? 'mine' : ''}`}>
                {team === myTeam ? t('sp.us') : t('sp.them')}: {view.teamScores[team] ?? 0}
                {playing && ` · ${view.tricksWon[team] ?? 0}`}
                {view.vulnerable[team] ? ' · V' : ''}
              </span>
            ))}
          </div>
          {bidding && (
            <div className="card glass" style={{ width: 'min(60%, 250px)', padding: 6 }} role="table" aria-label={t('br.auction')}>
              <div className="row" style={{ justifyContent: 'space-around', fontSize: 'var(--fs-xs)' }} role="row">
                {[0, 1, 2, 3].map((s) => (
                  <strong key={s} role="columnheader" style={{ minWidth: 42, textAlign: 'center', color: s === view.dealer ? 'var(--accent)' : undefined }}>
                    {COMPASS[s]}
                  </strong>
                ))}
              </div>
              {rows.map((row, i) => (
                <div key={i} className="row" style={{ justifyContent: 'space-around', fontSize: 'var(--fs-sm)' }} role="row">
                  {row.map((c, s) => (
                    <span key={s} role="cell" style={{ minWidth: 42, textAlign: 'center' }}>
                      {c ? <CallBadge call={c} /> : ' '}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )}
          {playing && (
            <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.dealNo}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
              {view.trick.map((p, i) => {
                const dir = seatPosition(p.seat, mySeat, 4).dir;
                const o = OFFSETS[dir]!;
                const off = { x: o.x * k, y: o.y * k };
                const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
                const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
                return (
                  <div
                    key={p.card}
                    className="slot fly"
                    style={{
                      transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                      ['--from-x' as string]: `${off.x * 3}px`,
                      ['--from-y' as string]: `${off.y * 4}px`,
                      ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                      zIndex: i,
                    }}
                  >
                    <PlayingCard id={p.card} size={trickSize} />
                  </div>
                );
              })}
            </div>
          )}
          {dealOver && dealt && (
            <div className="card glass" style={{ textAlign: 'center' }} role="status">
              <strong>{t('br.dealDone', { n: dealt.dealNo })}</strong>
              <div style={{ marginTop: 4 }}>
                {dealt.contract && dealt.score ? (
                  <>
                    {dealt.contract.level}
                    {STRAIN_SYMBOL[dealt.contract.strain]}
                    {dealt.contract.doubled === 1 ? ' X' : dealt.contract.doubled === 2 ? ' XX' : ''} {t('br.by', { name: COMPASS[dealt.contract.declarer]! })}{' '}
                    {dealt.score.made ? (dealt.score.overUnder > 0 ? `+${dealt.score.overUnder}` : '=') : `−${-dealt.score.overUnder}`}
                  </>
                ) : (
                  t('br.passedOut')
                )}
              </div>
              <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
                {[myTeam, 1 - myTeam].map((team) => (
                  <span key={team} className={`pill ${dealt.delta[team]! > 0 ? 'accent' : ''}`}>
                    {team === myTeam ? t('sp.us') : t('sp.them')}: {dealt.delta[team]! > 0 ? '+' : ''}{dealt.delta[team]}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{COMPASS[mySeat]} · {view.teamScores[myTeam] ?? 0} pts</> : null} />
      </div>
      <div className="table-bottom">
        {myTurn && legal?.kind === 'bid' && <BidBox view={view} onCall={(call) => void act({ type: 'call', call })} acting={acting} />}
        {dummyHand && playing && !iAmDummy && (
          <div aria-label={t('br.dummyHand', { seat: view.dummy !== null ? COMPASS[view.dummy]! : '' })}>
            <div className="center-note" style={{ textAlign: 'center', color: 'rgba(255,255,255,0.85)' }}>
              {t('br.dummyHand', { seat: view.dummy !== null ? COMPASS[view.dummy]! : '' })}
              {myTurn && playFrom !== null && playFrom === view.dummy && ` — ${t('br.playFromDummy')}`}
            </div>
            <Hand size="sm" label="Dummy" maxStep={0.7}>
              {dummyHand.map((c) => (
                <PlayingCard key={c} id={c} size="sm" playable={Boolean(myTurn && playFrom === view.dummy && playable.has(c))} unplayable={Boolean(myTurn && playFrom === view.dummy && !playable.has(c))} onClick={() => playFrom === view.dummy && playCard(c)} />
              ))}
            </Hand>
          </div>
        )}
        <Hand size={narrow ? 'md' : 'lg'} label="Your hand" maxStep={0.6}>
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size={narrow ? 'md' : 'lg'}
              dealIndex={bidding ? i : undefined}
              playable={Boolean(myTurn && playFrom === mySeat && playable.has(c))}
              unplayable={Boolean(myTurn && playFrom === mySeat && !playable.has(c))}
              onClick={() => playFrom === mySeat && playCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('br.contract')}</th>
              <th>N·S</th>
              <th>E·W</th>
            </tr>
          </thead>
          <tbody>
            {view.dealResults.map((r) => (
              <tr key={r.dealNo}>
                <td>D{r.dealNo}</td>
                <td>
                  {r.contract ? (
                    <>
                      {r.contract.level}
                      {STRAIN_SYMBOL[r.contract.strain]}
                      {r.contract.doubled === 1 ? 'X' : r.contract.doubled === 2 ? 'XX' : ''} {COMPASS[r.contract.declarer]}
                      <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{r.score!.made ? (r.score!.overUnder > 0 ? `+${r.score!.overUnder}` : '=') : `−${-r.score!.overUnder}`}</div>
                    </>
                  ) : (
                    t('br.passedOutShort')
                  )}
                </td>
                {[0, 1].map((team) => (
                  <td key={team} className="num">{r.delta[team]! > 0 ? `+${r.delta[team]}` : '–'}</td>
                ))}
              </tr>
            ))}
            <tr>
              <td colSpan={2}><strong>Total</strong></td>
              {view.teamScores.map((v, team) => (
                <td key={team} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('br.scoreHint', { deals: view.config.deals })}</p>
      </Modal>
    </>
  );
}

/** Bid box: pick a level, then a strain; Pass, double and redouble when allowed. */
function BidBox({ view, onCall, acting }: { view: BridgeView; onCall: (c: BridgeCall) => void; acting: boolean }) {
  const t = useT();
  const info = bridgeAuctionInfo(view.calls, view.seat!);
  const lowest = info.lastBid ? (info.lastBid.strain === 'N' ? info.lastBid.level + 1 : info.lastBid.level) : 1;
  const [level, setLevel] = useState(Math.min(7, lowest));
  const [sent, setSent] = useState(false);
  useEffect(() => {
    setLevel((l) => Math.max(Math.min(7, lowest), l));
  }, [lowest, view.calls.length]);
  const send = (c: BridgeCall) => {
    setSent(true);
    onCall(c);
    setTimeout(() => setSent(false), 1200);
  };
  return (
    <div className="bid-picker" role="group" aria-label={t('br.yourCall')}>
      <div className="bid-head">
        <strong>{t('br.yourCall')}</strong>
        <span className="dim">{info.lastBid ? `${t('br.last')} ${info.lastBid.level}${STRAIN_SYMBOL[info.lastBid.strain]}` : t('br.noBids')}</span>
      </div>
      <div className="bid-chips" role="radiogroup" aria-label={t('br.level')}>
        {[1, 2, 3, 4, 5, 6, 7].map((l) => (
          <button key={l} role="radio" aria-checked={level === l} className={`bid-chip ${level === l ? 'on' : ''}`} style={{ maxWidth: 40 }} disabled={l < lowest} onClick={() => setLevel(l)}>
            {l}
          </button>
        ))}
      </div>
      <div className="row" style={{ gap: 6 }} role="group" aria-label={t('br.strain')}>
        {STRAINS.map((s) => {
          const ok = bridgeBidAllowed(info.lastBid, level, s);
          return (
            <button key={s} className="btn grow" style={{ color: isRed(s) ? 'var(--danger)' : undefined, fontWeight: 700, padding: '8px 0' }} disabled={!ok || sent || acting} aria-label={`${level} ${s === 'N' ? 'No trump' : s === 'S' ? 'Spades' : s === 'H' ? 'Hearts' : s === 'D' ? 'Diamonds' : 'Clubs'}`} onClick={() => send({ kind: 'bid', level, strain: s })}>
              {level}
              {STRAIN_SYMBOL[s]}
            </button>
          );
        })}
      </div>
      <div className="row" style={{ gap: 6 }}>
        <button className="btn grow" disabled={sent || acting} onClick={() => send({ kind: 'pass' })}>
          {t('br.pass')}
        </button>
        <button className="btn grow gold" disabled={!info.canDouble || sent || acting} onClick={() => send({ kind: 'double' })}>
          X
        </button>
        <button className="btn grow gold" disabled={!info.canRedouble || sent || acting} onClick={() => send({ kind: 'redouble' })}>
          XX
        </button>
      </div>
    </div>
  );
}
